import { Link, withRouter, WithRouterProps } from '@client/router/compat';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHandWave } from '@fortawesome/pro-regular-svg-icons';
import { PATHS } from '@client/routes';
import {
  strategyGuideLinkWrapper,
  strategyGuideLinkIcon,
  strategyGuideLinkText,
  strategyGuideLinkProgress,
  strategyGuideLinkProgressValue,
  circleProgressSvg,
} from './strategyGuideLink.css';
import { Typography } from '@design-system/components/typography/typography';
import { sprinkles } from '@design-system/sprinkles.css';
import {
  StrategyGuideLinkVM,
  useStrategyGuideLinkViewModel,
} from './strategyGuideLink.viewModel';

type StrategyGuideLinkViewProps = {
  influencerId: number;
  vm: StrategyGuideLinkVM;
  pathname?: string;
};

const StrategyGuideLinkView = ({
  influencerId,
  vm,
  pathname = '',
}: StrategyGuideLinkViewProps) => {
  const guidePath = PATHS.GUIDE(influencerId);
  const isActive =
    guidePath === pathname ||
    guidePath === `/${pathname}` ||
    pathname.startsWith(guidePath);

  if (vm.isLoading) {
    return null;
  }

  if (!vm.hasActiveStrategy) {
    return null;
  }

  return (
    <Link to={guidePath} className={strategyGuideLinkWrapper({ isActive })}>
      <span className={strategyGuideLinkIcon}>
        <FontAwesomeIcon icon={faHandWave} fixedWidth />
      </span>
      <Typography
        variant="body"
        size="large"
        as="span"
        className={strategyGuideLinkText}
      >
        Strategy guide
      </Typography>
      <div className={strategyGuideLinkProgress}>
        <CircularProgress size={50} strokeWidth={5} progress={vm.progress} />
        <span className={strategyGuideLinkProgressValue}>{vm.progress}%</span>
      </div>
    </Link>
  );
};

type StrategyGuideLinkProps = {
  influencerId: number;
};

const StrategyGuideLinkRaw = ({
  influencerId,
  location,
}: StrategyGuideLinkProps & Partial<WithRouterProps>) => {
  const vm = useStrategyGuideLinkViewModel(influencerId);

  return (
    <StrategyGuideLinkView
      influencerId={influencerId}
      vm={vm}
      pathname={location?.pathname}
    />
  );
};

export const StrategyGuideLink = withRouter(StrategyGuideLinkRaw);

const CircularProgress = ({
  size,
  strokeWidth,
  progress,
}: {
  size: number;
  strokeWidth: number;
  progress: number;
}) => {
  const viewBox = `0 0 ${size} ${size}`;
  const radius = (size - strokeWidth) / 2;
  const circumference = Math.PI * radius * 2;
  const progressLength = (progress / 100) * circumference;

  return (
    <svg
      width={size}
      height={size}
      viewBox={viewBox}
      className={circleProgressSvg}
    >
      <circle
        fill="none"
        stroke="currentColor"
        className={sprinkles({ color: 'N-80' })}
        cx={size / 2}
        cy={size / 2}
        r={radius}
        strokeWidth={`${strokeWidth}px`}
      />
      <circle
        fill="none"
        stroke="currentColor"
        className={sprinkles({ color: 'SUCCESS-100' })}
        cx={size / 2}
        cy={size / 2}
        r={radius}
        strokeWidth={`${strokeWidth}px`}
        strokeDasharray={`${progressLength} ${circumference}`}
        strokeLinecap="round"
      />
    </svg>
  );
};
